import * as React from "react";
import { CourseInstructionPartOperations } from "../../../../course-author/instruction/parts/CourseInstructionPart.types";
import { DividerPart } from "../../../../course-author/instruction/parts/separator/DividerPart";
import { SpacerPart } from "../../../../course-author/instruction/parts/separator/SpacerPart";
import { PreferredLocale } from "../../../../../utils/locales";
import { HTMLRenderer } from "../../../../course-author/html-editor/Renderer";
import { ListItemsExercisePart } from "../../../../course-author/instruction/parts/exercises/list-items/ListItemsExercisePart.container";
import { OrderItems } from "../../../../course-author/instruction/parts/exercises/order-items/OrderItems.container";
import { ICourseInstructionContentPart } from "../../../../course-author/types";
import { Divider } from "../../FormElements";
import { ContentPreviewBox } from "./ContentPreviewBox";
import { ChoiceExercisePart } from "./Exercises/Choice/ChoiceExercisePart";
import { ClozeExercisePart } from "./Exercises/Cloze/ClozeExercisePart";
import { TeachingPart } from "./Teaching/index";
import {
  isClozePart,
  isDividerPart,
  isDocumentPart,
  isGridPart,
  isImagePart,
  isListWithOptionsPart,
  isListWithoutOptionsPart,
  isMultipleChoicePart,
  isSingleChoicePart,
  isSpacerPart,
  isTeachingPart,
  isUnorderedListPart,
} from "./util";
import ImagePart from "../../../../course-author/instruction/parts/image/ImagePart";
import DocumentPart from "../../../../course-author/instruction/parts/document/DocumentPart";

export interface IPartComponentProps {
  edit: boolean;
  part: ICourseInstructionContentPart;
  currentLocale: PreferredLocale;
  partOperations: CourseInstructionPartOperations;
  toggleEdit: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const PartComponent: React.FC<IPartComponentProps> = props => {
  const { currentLocale, edit, toggleEdit, part, partOperations } = props;

  if (part.deleted) {
    return null;
  }

  if (isTeachingPart(part)) {
    return (
      <TeachingPart
        currentLocale={currentLocale}
        edit={edit}
        teachingOperations={partOperations}
        teachingPart={part}
        toggleEdit={toggleEdit}
        containerRef={props.containerRef}
      />
    );
  }

  if (isMultipleChoicePart(part) || isSingleChoicePart(part)) {
    return (
      <ChoiceExercisePart
        choiceExercisePart={part}
        currentLocale={currentLocale}
        edit={edit}
        partOperations={partOperations}
      />
    );
  }

  if (isClozePart(part)) {
    return (
      <ClozeExercisePart
        clozeExercisePart={part}
        currentLocale={currentLocale}
        edit={edit}
        partOperations={partOperations}
      />
    );
  }

  if (isUnorderedListPart(part) || isListWithoutOptionsPart(part)) {
    return (
      <ListItemsExercisePart
        currentLocale={currentLocale}
        exercisePart={part}
        isEditing={edit}
        partOperations={partOperations}
      />
    );
  }

  if (isListWithOptionsPart(part)) {
    return (
      <OrderItems currentLocale={currentLocale} exercisePart={part} isEditing={edit} partOperations={partOperations} />
    );
  }

  if (isDividerPart(part)) {
    return <DividerPart dividerPart={part} partOperations={partOperations} edit={edit} />;
  }

  if (isSpacerPart(part)) {
    return <SpacerPart spacerPart={part} partOperations={partOperations} edit={edit} />;
  }

  if (isImagePart(part)) {
    return (
      <ImagePart
        key={part.content.settings.fileImageId || ""}
        imagePart={part}
        partOperations={partOperations}
        edit={edit}
      />
    );
  }

  if (isDocumentPart(part)) {
    return <DocumentPart documentPart={part} partOperations={partOperations} edit={edit} />;
  }

  // NOTE: Grids are not displayed using this function
  if (isGridPart(part)) {
    return null;
  }

  return (
    <React.Fragment key={part.id}>
      <Divider />
      <ContentPreviewBox>
        <HTMLRenderer source={`<pre>${JSON.stringify(part, null, 2)}</pre>`} />
      </ContentPreviewBox>
    </React.Fragment>
  );
};
